import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, ArrowUp, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const SUGGESTIONS = [
  "What does Zenspark do?",
  "What are Extended PODs?",
  "Which engagement models do you offer?",
];

export const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Zia — Zenspark's AI assistant. Ask me about our AI transformation, Extended PODs, engagement models or how we help mid-market enterprises grow." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-zia", openHandler);
    return () => window.removeEventListener("open-zia", openHandler);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("zia-open", open);
    return () => document.body.classList.remove("zia-open");
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } catch (err) { /* ignore parse errors */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 grid h-14 w-14 place-items-center rounded-full bg-navy text-white shadow-[0_10px_40px_rgba(27,26,36,0.35)] dark:bg-accent"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Chat with Zia"
      >
        <AnimatePresence mode="wait">
          {open ? <X key="x" className="h-6 w-6" /> : <MessageSquare key="m" className="h-6 w-6" />}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-50 flex h-[560px] max-h-[calc(100vh-7rem)] w-[calc(100vw-3rem)] max-w-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_20px_60px_rgba(27,26,36,0.28)]"
          >
            <div className="flex items-center gap-3 border-b border-border bg-navy px-5 py-4 text-white">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-accent">
                <Sparkles className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-sm font-semibold">Zia</p>
                <p className="text-xs text-white/60">Zenspark AI Assistant</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-navy text-white dark:bg-accent"
                        : "bg-secondary text-foreground"
                    }`}
                  >
                    {m.content || <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span>}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion"
                      onClick={() => send(s)}
                      className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-accent hover:text-accent"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-border p-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about our services..."
                className="flex-1 rounded-full bg-secondary px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <button
                data-testid="chat-send"
                type="submit"
                disabled={busy}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-navy text-white transition-transform hover:scale-105 disabled:opacity-50 dark:bg-accent"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Dot = ({ d = 0 }) => (
  <motion.span
    className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground"
    animate={{ opacity: [0.3, 1, 0.3] }}
    transition={{ duration: 1, repeat: Infinity, delay: d }}
  />
);
